import { useCallback, useEffect, useRef, useState } from "react";

import {
  accountScope,
  type ApiErrorLike,
  type FetchPage,
  type ListCache,
  isApiErrorLike,
} from "@heraion/shared";

// Cursor-paginated list with stale-while-revalidate. Cache hydrates
// instantly on mount, fresh fetch lands on top, loadMore walks the
// cursor on demand.
//
// Lifted here from apps/mobile 2026-09-18 (CN-47): it is the ONLY
// correct list primitive in the app, and while it lived in apps/mobile
// neither packages/ui nor apps/web-rnw could import it — so every new
// surface on those two either re-invented the loop or drained the
// server with a fixed `limit`. Moving it is the fix for that class.
//
// Separation of concerns:
//   - paginate() / FetchPage / ListCache live in packages/shared so
//     web and mobile share the contract.
//   - the cache is INJECTED (opts.cache), so this hook binds to no
//     storage backend: AsyncStorage on native, localStorage on web.
//   - useAsyncList owns the React state machine + cancellation.
//
// Errors: the hook exposes `ApiErrorLike` (the `{status, body, message}`
// shape every surface's ApiError class shares) rather than any one
// surface's class — that is what lets it live here. Callers that want
// the concrete class still `instanceof` it downstream.
//
// Throttling: apiFetch (the layer fetchPage calls) already retries
// 429/503 with exponential backoff. We don't re-retry here.
//
// Abort: every refresh/loadMore allocates a fresh AbortController.
// Re-entering refresh while one is in flight cancels the older — the
// most-recent request always wins. The signal is forwarded to
// fetchPage so the underlying request can short-circuit too.

export type AsyncListStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "loading_more"
  | "refreshing"
  | "error";

export type UseAsyncListOptions<T, C> = {
  /** Stable identifier for cache keying and effect dedupe. */
  key: string;
  fetchPage: FetchPage<T, C>;
  cache?: ListCache<T>;
  /** Run on mount. Default true. */
  immediate?: boolean;
};

export type AsyncListState<T, C> = {
  items: T[] | null;
  status: AsyncListStatus;
  error: ApiErrorLike | null;
  cursor: C | null;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
};

// A non-ApiError failure (transport, abort, bug) still has to satisfy
// `.status` for callers that render `api_error_${status}`. 0 is the
// same sentinel apps/mobile's ApiError used before the lift, so the
// rendered code is unchanged.
const toApiErrorLike = (err: unknown): ApiErrorLike => {
  if (isApiErrorLike(err)) return err;
  const wrapped = new Error(
    err instanceof Error ? err.message : "unknown",
  ) as Error & { status: number; body: unknown };
  wrapped.status = 0;
  wrapped.body = null;
  return wrapped;
};

export function useAsyncList<T, C = string>(
  opts: UseAsyncListOptions<T, C>,
): AsyncListState<T, C> {
  const { key, fetchPage, cache, immediate = true } = opts;
  const [items, setItems] = useState<T[] | null>(null);
  const [cursor, setCursor] = useState<C | null>(null);
  const [status, setStatus] = useState<AsyncListStatus>("idle");
  const [error, setError] = useState<ApiErrorLike | null>(null);
  // CN-48 — ONE controller per OPERATION, never one shared by both.
  // refresh() and loadMore() can be in flight at the same time (that is
  // the documented race generationRef defends the DATA side of), so a
  // single abortRef made cancellation asymmetric: refresh aborted
  // whatever was there, loadMore overwrote it WITHOUT aborting. Two
  // consequences, both real: a refresh started before a loadMore became
  // unreachable — unmount aborted only the loadMore controller sitting
  // in the ref, and the refresh kept running and could commit onto an
  // unmounted hook; and a loadMore in flight was cancelled by any
  // refresh, silently losing the page the athlete had scrolled for.
  //
  // Two refs fix both without deciding "who wins": each operation only
  // ever aborts its OWN previous attempt, they never cancel each other
  // (ordering BETWEEN them is generationRef's job, not the signal's),
  // and unmount aborts both. See test-ledger T-15 for the repro.
  const refreshAbortRef = useRef<AbortController | null>(null);
  const loadMoreAbortRef = useRef<AbortController | null>(null);
  // Pin the fetchPage + cache refs so the load/refresh callbacks
  // stay stable across renders. The hook contract is "treat opts as
  // an initial config" — recreating fetchPage every render shouldn't
  // re-run the effect. Same pattern WorkoutsContext uses for refresh.
  const fetchPageRef = useRef(fetchPage);
  const cacheRef = useRef(cache);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
    cacheRef.current = cache;
  }, [fetchPage, cache]);
  // generationRef is what defends refresh/loadMore against each other.
  // refresh() bumps it when its page actually LANDS and is about to be
  // committed — that is the act that invalidates whatever pagination
  // sequence was running before it, because only then does the new
  // sequence (new cursor, new items) actually replace the old one.
  //
  // Bumping any earlier — at refresh's own call-start — cannot work: the
  // documented race has loadMore starting WHILE a refresh is already in
  // flight (status is already "refreshing" when loadMore's guard lets it
  // through), i.e. after refresh's start has already run. A loadMore
  // that captures generationRef.current at its own start would then
  // capture the value refresh's start-bump already produced, and nothing
  // would change it again before that loadMore's response lands — no
  // mismatch, no discard, bug not fixed. Verified by hand: bumping at
  // refresh's call-start left the race test red. Bumping at commit also
  // avoids a second false positive: a refresh that FAILS or gets aborted
  // never replaces anything, so it must not invalidate an otherwise-good
  // concurrent loadMore either.
  //
  // loadMore() captures the generation at its own start; if it has
  // changed by the time its response lands, a refresh has committed
  // since, so it exits untouched: no items merge, no setCursor, no
  // cache.write, no status write (the refresh that won already left the
  // right status behind).
  const generationRef = useRef(0);
  // itemsRef mirrors `items`, written synchronously at the same call
  // site as every setItems. It is NOT there to defend the race above —
  // generationRef does that now, by making a superseded loadMore bail
  // before it ever reads this ref. It is there because `setItems(prev =>
  // ...)`'s updater does not run synchronously: React 18 batches the
  // flush, so a value captured out of the updater (`let next; setItems(p
  // => { next = ...; return next })`) is still unset on the very next
  // line — verified by hand, it broke the existing "loadMore writes the
  // accumulated list to the cache" test the same way. loadMore needs the
  // merged array synchronously, in the same tick, to hand to
  // cache.write — itemsRef is the only synchronous source for that.
  const itemsRef = useRef<T[] | null>(null);
  const commitItems = useCallback((next: T[] | null) => {
    itemsRef.current = next;
    setItems(next);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    refreshAbortRef.current?.abort();
    const ctrl = new AbortController();
    refreshAbortRef.current = ctrl;
    setStatus((prev) => (prev === "loaded" ? "refreshing" : "loading"));
    setError(null);
    // #398: the account this read is for; the copy is written under it, so a
    // refresh with no cache read before it (`immediate: false`) still writes.
    const scope = accountScope();
    try {
      const page = await fetchPageRef.current(null, ctrl.signal);
      if (ctrl.signal.aborted) return;
      // The commit below is what starts the new pagination sequence, so
      // this is where the generation bumps — see the generationRef
      // comment above for why "at commit", not "at call-start".
      generationRef.current += 1;
      commitItems(page.items);
      setCursor(page.cursor);
      setStatus("loaded");
      if (cacheRef.current) {
        void cacheRef.current.write(page.items, scope);
      }
    } catch (e) {
      if (ctrl.signal.aborted) return;
      setStatus("error");
      setError(toApiErrorLike(e));
    }
  }, [commitItems]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (cursor === null) return;
    if (status === "loading_more" || status === "loading") return;
    const generation = generationRef.current;
    loadMoreAbortRef.current?.abort();
    const ctrl = new AbortController();
    loadMoreAbortRef.current = ctrl;
    setStatus("loading_more");
    const scope = accountScope();
    try {
      const page = await fetchPageRef.current(cursor, ctrl.signal);
      if (ctrl.signal.aborted || generationRef.current !== generation) return;
      // #423: itemsRef holds the list of the account the page was read for.
      if (!scope.current()) {
        // The list in memory is the old account's: drop it, and the
        // "loading_more" this call set, which nothing else would clear.
        commitItems(null);
        setCursor(null);
        setStatus("idle");
        return;
      }
      // itemsRef, not the closure's `items`: see the itemsRef comment
      // above — this read is safe from the refresh race (a superseded
      // loadMore already returned above) and from React 18's batched
      // functional-update timing (this IS the synchronous source).
      const next = [...(itemsRef.current ?? []), ...page.items];
      commitItems(next);
      // The cache must mirror what the athlete has actually scrolled to,
      // not just page 1 — else a cold reopen loses everything loadMore fetched.
      if (cacheRef.current) void cacheRef.current.write(next, scope);
      setCursor(page.cursor);
      setStatus("loaded");
    } catch (e) {
      if (ctrl.signal.aborted || generationRef.current !== generation) return;
      setStatus("error");
      setError(toApiErrorLike(e));
    }
  }, [cursor, status, commitItems]);

  // Mount: hydrate cache, then refresh. Reruns when the key changes
  // so a caller-driven cache-key bump (e.g. scope toggle) re-mounts
  // the data without re-mounting the hook.
  useEffect(() => {
    if (!immediate) return;
    let cancelled = false;
    void (async () => {
      if (cacheRef.current) {
        const cached = await cacheRef.current.read();
        if (cancelled) return;
        if (cached !== null) {
          commitItems([...cached]);
          setStatus("loaded");
        }
      }
      if (cancelled) return;
      void refresh();
    })();
    return () => {
      cancelled = true;
      refreshAbortRef.current?.abort();
      loadMoreAbortRef.current?.abort();
    };
  }, [key, immediate, refresh, commitItems]);

  return {
    items,
    status,
    error,
    cursor,
    hasMore: cursor !== null,
    refresh,
    loadMore,
  };
}
